<template>
  <div class="w-full">
    <div class="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-sm">
      <div class="flex items-start justify-between">
        <div>
          <div class="text-sm font-semibold" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiPhoneIvrSetup.phone.title', 'Communication Phone Number') }}</div>
          <div class="mt-1 text-xs text-base-content opacity-70" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiPhoneIvrSetup.phone.description', 'Choose the connected phone number this IVR setup should use.') }}</div>
        </div>
        <span class="badge badge-outline">{{ selectedPhoneDisplay }}</span>
      </div>
      <div class="mt-4 flex flex-wrap items-center">
        <button type="button" class="btn btn-sm" @click="openPhoneSelection()">{{ selectedPhoneSourceId !== null ? t('aiPhoneIvrSetup.phone.change', 'Change Number') : t('aiPhoneIvrSetup.phone.select', 'Select Number') }}</button>
        <button v-if="selectedPhoneSourceId !== null" type="button" class="btn btn-sm btn-ghost text-error" style="margin-inline-start: 8px;" @click="clearSelection()">{{ t('aiPhoneIvrSetup.phone.clear', 'Clear') }}</button>
        <button type="button" class="btn btn-sm btn-ghost" style="margin-inline-start: 8px;" @click="router.push('/communicationSources')">{{ t('aiPhoneIvrSetup.phone.manage', 'Manage Numbers') }}</button>
      </div>
    </div>
    <WhatsAppNumberPickerModal
      :visible="showPhoneModal"
      :items="availablePhone"
      :loading="loadingPhone"
      :currentId="selectedPhoneSourceId"
      mode="phone"
      @close="cancelPhone"
      @confirm="onPhoneConfirm"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import WhatsAppNumberPickerModal from '@/components/WhatsAppNumberPickerModal.vue';

const props = defineProps({
  phoneSourceId: {
    type: [Number, String],
    default: null
  },
  phoneNumber: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:phoneSourceId', 'update:phoneNumber']);

const router = useRouter();
const toast = useToast();
const { t, currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);
const phoneSourceTypes = ['twilioPhone', 'phonePartner', 'phoneCustomSip', 'sipTrunk', 'sonarixPhone', 'genovisePhone', 'phone'];

const showPhoneModal = ref(false);
const loadingPhone = ref(false);
const availablePhone = ref([]);
const selectedPhoneSourceId = ref(normalizeSourceId(props.phoneSourceId));
const selectedPhoneDisplay = ref(typeof props.phoneNumber === 'string' ? props.phoneNumber.trim() : '');

function normalizeSourceId(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed) !== true) {
    return null;
  }
  return Math.floor(parsed);
}

function isSameSourceId(left, right) {
  const normalizedLeft = normalizeSourceId(left);
  const normalizedRight = normalizeSourceId(right);
  if (normalizedLeft === null || normalizedRight === null) {
    return false;
  }
  return normalizedLeft === normalizedRight;
}

async function openPhoneSelection() {
  showPhoneModal.value = true;
  loadingPhone.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/available`, { params: { type: 'phoneNumber' } });
    const raw = Array.isArray(response.data) === true ? response.data : [];
    availablePhone.value = raw.filter((item) => phoneSourceTypes.includes(String(item.type)) === true).map((item) => {
      const sourceIdRaw = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
      const sourceId = normalizeSourceId(sourceIdRaw);
      return {
        sourceId: sourceId,
        type: item.type,
        display: item.display !== undefined && item.display !== null ? item.display : (item.name !== undefined && item.name !== null ? item.name : ''),
        number: item.number !== undefined && item.number !== null ? item.number : ''
      };
    }).filter((item) => item.sourceId !== null);
    const currentId = selectedPhoneSourceId.value !== null ? selectedPhoneSourceId.value : normalizeSourceId(props.phoneSourceId);
    if (currentId !== null) {
      let exists = false;
      for (let index = 0; index < availablePhone.value.length; index = index + 1) {
        if (isSameSourceId(availablePhone.value[index].sourceId, currentId) === true) {
          exists = true;
          break;
        }
      }
      if (exists === false) {
        availablePhone.value.unshift({
          sourceId: currentId,
          type: '',
          display: selectedPhoneDisplay.value !== '' ? selectedPhoneDisplay.value : String(currentId),
          number: selectedPhoneDisplay.value
        });
      }
    }
  } catch (error) {
    availablePhone.value = [];
    toast.error(t('aiPhoneIvrSetup.phone.loadFailed', 'Failed to load available phone numbers.'));
  } finally {
    loadingPhone.value = false;
  }
}

function cancelPhone() {
  showPhoneModal.value = false;
}

function clearSelection() {
  selectedPhoneSourceId.value = null;
  selectedPhoneDisplay.value = '';
  emit('update:phoneSourceId', null);
  emit('update:phoneNumber', '');
}

function onPhoneConfirm(payload) {
  if (payload === null || payload === undefined) {
    return;
  }
  const rawSourceId = payload.sourceId;
  if (rawSourceId === null || rawSourceId === undefined || Number.isFinite(Number(rawSourceId)) !== true) {
    clearSelection();
    showPhoneModal.value = false;
    return;
  }
  const nextSourceId = parseInt(String(rawSourceId).trim(), 10);
  const nextNumber = typeof payload.number === 'string' ? payload.number : '';
  selectedPhoneSourceId.value = nextSourceId;
  selectedPhoneDisplay.value = nextNumber;
  emit('update:phoneSourceId', nextSourceId);
  emit('update:phoneNumber', nextNumber);
  showPhoneModal.value = false;
}

watch(() => props.phoneSourceId, (value) => {
  const normalizedValue = normalizeSourceId(value);
  if (normalizedValue !== selectedPhoneSourceId.value) {
    selectedPhoneSourceId.value = normalizedValue;
  }
});

watch(() => props.phoneNumber, (value) => {
  selectedPhoneDisplay.value = typeof value === 'string' ? value.trim() : '';
});
</script>
